import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  IconButton,
  Grid,
  Divider,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Event as EventIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Notes as NotesIcon,
  Repeat as RepeatIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import { vi } from "date-fns/locale";
import { getToken } from "services/auth.service";

const EventDetailPopover = ({ open, onClose, event, onEdit, onDelete }) => {
  const [eventDetail, setEventDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    const fetchEventDetail = async () => {
      setLoading(true);
      setError("");
      try {
        const token = getToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/Events/${event.id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Không thể tải chi tiết sự kiện");
        }

        const data = await response.json();
        setEventDetail(data);
      } catch (error) {
        console.error("Error fetching event detail:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    if (open && event?.id) {
      fetchEventDetail();
    }
  }, [open, event]);

  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/Events/${event.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Không thể xóa sự kiện");
      }

      onDelete && onDelete(event.id);
      setDeleteDialogOpen(false);
      onClose();
    } catch (error) {
      console.error("Error deleting event:", error);
      setError(error.message);
      setDeleteDialogOpen(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
  };

  const handleEdit = () => {
    onEdit && onEdit(eventDetail);
    onClose();
  };

  const formatDateTime = (dateString, isAllDay = false) => {
    const date = new Date(dateString);
    if (isAllDay) {
      return format(date, "dd/MM/yyyy", { locale: vi });
    }
    return format(date, "dd/MM/yyyy HH:mm", { locale: vi });
  };

  const getEventTypeLabel = (type) => {
    switch (type) {
      case "manual":
        return "Thủ công";
      case "contract":
        return "Từ hợp đồng";
      case "recurring":
        return "Lặp lại";
      default:
        return type;
    }
  };

  const getRecurrenceLabel = (pattern) => {
    switch (pattern) {
      case "daily":
        return "Hàng ngày";
      case "weekly":
        return "Hàng tuần";
      case "monthly":
        return "Hàng tháng";
      case "yearly":
        return "Hàng năm";
      default:
        return pattern;
    }
  };

  if (!event) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xs"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: 2 } } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <EventIcon sx={{ color: event.backgroundColor || "#2196f3" }} />
            <Typography variant="h6" component="div">
              Chi tiết sự kiện
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : eventDetail ? (
            <Grid container spacing={2}>
              {/* Thông tin cơ bản */}
              <Grid item size={{ xs: 12 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="h5" gutterBottom>
                    {eventDetail.title}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                    <Chip
                      label={getEventTypeLabel(eventDetail.eventType)}
                      size="small"
                      color="primary"
                    />
                    {eventDetail.isRecurring && (
                      <Chip
                        icon={<RepeatIcon />}
                        label="Lặp lại"
                        size="small"
                        color="secondary"
                      />
                    )}
                  </Box>
                </Box>
              </Grid>

              {/* Thời gian */}
              <Grid item size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Thời gian
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" gutterBottom>
                    <strong>Bắt đầu:</strong>{" "}
                    {formatDateTime(
                      eventDetail.startDate,
                      eventDetail.isAllDay
                    )}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Kết thúc:</strong>{" "}
                    {formatDateTime(eventDetail.endDate, eventDetail.isAllDay)}
                  </Typography>
                </Box>

                {eventDetail.isAllDay && (
                  <Typography variant="body2" color="text.secondary">
                    Cả ngày
                  </Typography>
                )}
              </Grid>

              {/* Lặp lại */}
              {eventDetail.isRecurring && (
                <Grid item size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Lặp lại
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Kiểu:</strong>{" "}
                    {getRecurrenceLabel(eventDetail.recurrencePattern)}
                  </Typography>
                  {eventDetail.recurrenceEndDate && (
                    <Typography variant="body2">
                      <strong>Đến:</strong>{" "}
                      {formatDateTime(eventDetail.recurrenceEndDate, true)}
                    </Typography>
                  )}
                </Grid>
              )}

              {/* Mô tả */}
              {eventDetail.description && (
                <Grid item size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Mô tả
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {eventDetail.description}
                  </Typography>
                </Grid>
              )}

              {/* Hợp đồng */}
              {eventDetail.contractId && (
                <Grid item size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    <BusinessIcon sx={{ mr: 0.5, fontSize: 18 }} />
                    Hợp đồng
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Số HĐ:</strong> {eventDetail.contractNumber}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Tên:</strong> {eventDetail.contractName}
                  </Typography>
                  {eventDetail.itemId && (
                    <>
                      <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
                        <strong>Mã hạng mục:</strong> {eventDetail.itemCode}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Tên hạng mục:</strong> {eventDetail.itemName}
                      </Typography>
                    </>
                  )}
                </Grid>
              )}

              {/* Người tạo */}
              <Grid item size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  <PersonIcon sx={{ mr: 0.5, fontSize: 18 }} />
                  Thông tin tạo
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" gutterBottom>
                    <strong>Người tạo:</strong> {eventDetail.createdByName}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Ngày tạo:</strong>{" "}
                    {formatDateTime(eventDetail.createdAt)}
                  </Typography>
                </Box>
              </Grid>

              {/* Ghi chú */}
              {eventDetail.notes && (
                <Grid item size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    <NotesIcon sx={{ mr: 0.5, fontSize: 18 }} />
                    Ghi chú
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {eventDetail.notes}
                  </Typography>
                </Grid>
              )}

              {/* Người được chia sẻ */}
              {eventDetail.sharedUsers &&
                eventDetail.sharedUsers.length > 0 && (
                  <Grid item size={{ xs: 12 }}>
                    <Divider sx={{ my: 2 }} />
                    <Typography
                      variant="subtitle2"
                      color="primary"
                      gutterBottom
                    >
                      Chia sẻ với ({eventDetail.sharedUsers.length})
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {eventDetail.sharedUsers.map((user) => (
                        <Chip
                          key={user.userId}
                          label={user.fullName}
                          size="small"
                          variant="outlined"
                          title={user.email}
                        />
                      ))}
                    </Box>
                  </Grid>
                )}
            </Grid>
          ) : null}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} color="inherit">
            Đóng
          </Button>
          {eventDetail?.canEdit && (
            <>
              <Button
                onClick={handleEdit}
                startIcon={<EditIcon />}
                color="primary"
                variant="outlined"
              >
                Chỉnh sửa
              </Button>
              <Button
                onClick={handleDelete}
                startIcon={<DeleteIcon />}
                color="error"
                variant="outlined"
              >
                Xóa
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        maxWidth="xs"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: 2 } } }}
      >
        <DialogTitle>
          <Typography variant="h6" component="div">
            Xác nhận xóa sự kiện
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Bạn có chắc chắn muốn xóa sự kiện "{eventDetail?.title}" không?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Hành động này không thể hoàn tác.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCancelDelete} color="inherit">
            Hủy
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            sx={{
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            Xóa
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EventDetailPopover;
